import React, { memo } from "react";
import { Pressable } from "react-native";
import { Feather } from "@expo/vector-icons";
import { AppTextInput, type AppTextInputProps } from "@/components/ui/TextInput";
import { useI18n } from "@/context/I18nContext";
import { Icon } from "@/components/ui/Icon";
import { TranslationKeys } from "@chinese-vendor/i18n";

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SearchInputProps extends Omit<
  AppTextInputProps,
  "leadingIcon" | "trailingIcon" | "label"
> {
  /** Current search string. */
  value: string;
  label?: TranslationKeys;
  /** Called on each keystroke and also called with "" when the clear button is pressed. */
  onChangeText: (text: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * A controlled text input pre-configured for search use-cases.
 *
 * - Magnifying-glass icon on the leading edge (always visible).
 * - `×` clear button on the trailing edge (only visible when `value` is non-empty).
 *
 * All other `AppTextInput` props (label, hint, error, placeholder, …) are
 * forwarded transparently. `leadingIcon` and `trailingIcon` are reserved by
 * this component and cannot be overridden.
 *
 * @example
 * const [query, setQuery] = useState("");
 *
 * <SearchInput
 *   label="Search"
 *   value={query}
 *   onChangeText={setQuery}
 *   placeholder="Search countries…"
 * />
 */
export const SearchInput = memo(function SearchInput({
  value,
  onChangeText,
  ...rest
}: SearchInputProps) {
  const { t } = useI18n();
  return (
    <AppTextInput
      value={value}
      containerClassName="mb-0"
      onChangeText={onChangeText}
      leadingIcon={<Icon as={Feather} name="search" size={18} className="text-stroke-70" />}
      trailingIcon={
        value.length > 0 ? (
          <Pressable
            onPress={() => onChangeText("")}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t("common.clearSearch")}
          >
            <Icon as={Feather} name="x" size={18} className="text-dark-text-50" />
          </Pressable>
        ) : undefined
      }
      {...rest}
    />
  );
});
